import { Info, Search } from "react-feather";
import LoadingSpinner from "../../../LoadingSpinner";
import SearchDropdown from "../../../input/SearchDropdown";
import { getBehalfOf } from "../../../../api/systemUserApi";
import {
  getBulkDropdownData,
  getOtherRefSearchDropdown,
} from "../../../../api/keyvalue";
import Input from "../../../input/Input";
import Button from "../../../input/Button";
import Grid from "../../../Grid";
import {
  ActiveCellStyle,
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../../renderer/StyleRenderer";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  searchUserSupplierNegotiationApprovals,
  supplierNegotiationApprovalAmountEdit,
  supplierNegotiationApprovalQtyEdit,
  userSupplierNegotiationAdvancedSearch,
  userSupplierNegotiationApproval,
} from "../../../../api/userSupplierNegotiationApi";
import { errorMessageToast } from "../../../../helper";
import { toast } from "react-toastify";
import AlertConfirmation from "../../../input/AlertConfirmation";
import Tab from "../../../Tab";
import Dropdown from "../../../input/Dropdown";

const CELL_EDIT_KEYS = {
  AMOUNT: "jotCharge",
  QTY: "jotQty",
};

const SupplierNegotiationApproval = () => {
  const [activeAccordion, setActiveAccordion] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadDates, setReloadDates] = useState(true);
  const user = useSelector((state) => state.user);
  const defaultState = {
    user: user.username,
    userId: "",
    customer: "",
    fromDate: null,
    toDate: null,
    status: 1,
  };
  const [searchRequest, setSearchRequest] = useState(defaultState);
  const [searchClicked, setSearchClicked] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentSearchRequest, setCurrentSearchRequest] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [updateRequest, setUpdateRequest] = useState(null);
  const [cellType, setCellType] = useState(null);
  const [oldState, setOldState] = useState({});
  const [isCellUpdateLoading, setIsCellUpdateLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [advancedReqNos, setAdvancedReqNos] = useState("");

  useEffect(() => {
    if (reloadDates) {
      const aWeekAgo = new Date();
      aWeekAgo.setDate(aWeekAgo.getDate() - 7);
      const formattedWeekAgo = aWeekAgo.toISOString().split("T")[0];
      const formattedToday = new Date().toISOString().split("T")[0];
      console.log(formattedToday, formattedWeekAgo);
      setSearchRequest((prev) => ({
        ...prev,
        fromDate: formattedWeekAgo,
        toDate: formattedToday,
      }));
    }
    setReloadDates(false);
  }, [reloadDates]);

  useEffect(() => {
    (async () => {
      const { status = [] } = await getBulkDropdownData([{ key: "status" }]);

      setStatusOptions(status);
    })();
  }, []);

  const customerRef = useRef(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    await searchHandler();
  };

  const searchHandler = async () => {
    setCurrentSearchRequest(searchRequest);
    setIsLoading(true);
    setSearchClicked(true);
    setActiveAccordion(1);

    try {
      const data = await searchUserSupplierNegotiationApprovals(searchRequest);
      setSearchResults(data);
    } catch (e) {
      errorMessageToast(e);
    }
    setIsLoading(false);
  };

  const handleReset = () => {
    setSearchRequest(defaultState);
    setSearchClicked(false);
    customerRef.current?.reset();
    setSearchResults([]);
    setReloadDates(true);
  };

  const advancedSearchReset = () => {
    setAdvancedReqNos("");
    setSearchClicked(false);
    setSearchResults([]);
  };

  const gridRef = useRef();

  const handleSelectionChanged = useCallback(() => {
    const selectedData = gridRef.current.getSelectedData();
    setSelectedRows(selectedData);
  }, []);

  const actionMenuHandler = async (data) => {
    setIsLoading(true);

    const request = selectedRows.reduce((acc, item) => {
      acc[item.jotId] = data.value;
      return acc;
    }, {});

    try {
      await userSupplierNegotiationApproval(request);
      const data =
        await searchUserSupplierNegotiationApprovals(currentSearchRequest);
      setSearchResults(data);
      toast.success(
        `${data.value === 2 ? "Approved" : "Rejected"} successfully!`,
        {
          autoClose: 5000,
          containerId: "Modal",
        },
      );
    } catch (error) {
      errorMessageToast(error);
    }
    setIsLoading(false);
  };

  const cellEditingSubmitHandler = (data) => {
    if (
      data.column.colId === CELL_EDIT_KEYS.AMOUNT ||
      data.column.colId === CELL_EDIT_KEYS.QTY
    ) {
      if (!data.valueChanged) {
        toast.warn(
          `The ${data.column.colId === CELL_EDIT_KEYS.AMOUNT ? "Amount" : "Qty"} is unchanged`,
          {
            autoClose: 5000,
            containerId: "Main",
          },
        );
        return;
      }
      setCellType(data.column.colId);
      setUpdateRequest({
        id: data.data.jotId,
        value: data.value,
      });
      setAlertOpen(true);
    }
  };

  const advancedSearch = async () => {
    setIsLoading(true);
    setActiveAccordion(2);
    setSearchClicked(true);
    try {
      const data = { reqNos: advancedReqNos };
      const results = await userSupplierNegotiationAdvancedSearch(data);
      setSearchResults(results);
    } catch (error) {
      console.error("Advanced search error", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AlertConfirmation
        onConfirm={async () => {
          setIsCellUpdateLoading(true);
          try {
            const message =
              cellType === CELL_EDIT_KEYS.AMOUNT
                ? await supplierNegotiationApprovalAmountEdit(updateRequest)
                : await supplierNegotiationApprovalQtyEdit(updateRequest);

            toast.success(message, { autoClose: 5000, containerId: "Main" });
          } catch (e) {
            console.error(e);
            errorMessageToast(e);
          } finally {
            setIsCellUpdateLoading(false);
            setAlertOpen(false);
            await searchHandler();
          }
        }}
        onCancel={async () => {
          setOldState({});
          setAlertOpen(false);
          await searchHandler();
        }}
        loadingMessage="Updating..."
        message="Are you sure you want to update?"
        isLoading={isCellUpdateLoading}
        open={alertOpen}
      />
      <div className="ui tabular menu top attached">
        <Tab
          Icon={Info}
          onClick={() => {}}
          active={true}
          label="Supplier Negotiation Approval"
        />
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: "block" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion ? "active" : ""}`}
            onClick={() => {
              setActiveAccordion((prev) => {
                if (prev === 1) {
                  return null;
                }

                setSearchClicked(false);
                setSearchResults([]);
                return 1;
              });
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>
            </div>
          </div>
          <div className={`content ${activeAccordion === 1 ? "active" : ""}`}>
            <form className="ui form">
              <h4 className="ui dividing header">
                Supplier Negotiation Approval
              </h4>
              <div className="five fields">
                <SearchDropdown
                  key={1}
                  multiGrid={true}
                  multiGridCurrentValue={searchRequest.user}
                  setReset={() => {}}
                  reset={false}
                  api={getBehalfOf}
                  apiDependency={{
                    customer: null,
                    shipper: null,
                  }}
                  clearable={true}
                  onClear={() => {
                    setSearchRequest((prevState) => ({
                      ...prevState,
                      user: user.username,
                      userId: null,
                    }));
                  }}
                  returnFullObject={true}
                  onResultSelect={(data) => {
                    console.log(data);
                    setSearchRequest((prevState) => ({
                      ...prevState,
                      user: data.usrUsername,
                      userId: data.usrId,
                    }));
                  }}
                  label="User"
                />
                <SearchDropdown
                  ref={customerRef}
                  key={1}
                  clearable={true}
                  setReset={() => {}}
                  reset={false}
                  onClear={() => {
                    setSearchRequest((prevState) => ({
                      ...prevState,
                      customer: null,
                    }));
                  }}
                  onResultSelect={(data) => {
                    setSearchRequest((prevState) => ({
                      ...prevState,
                      customer: data,
                    }));
                  }}
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "customer",
                  }}
                  label="Customer"
                />
                <Input
                  required={true}
                  type="date"
                  label="From Date"
                  name="fromDate"
                  max={searchRequest.toDate ?? null}
                  value={searchRequest.fromDate}
                  onChange={(e) => {
                    setSearchRequest((prev) => ({
                      ...prev,
                      fromDate: e.target.value,
                    }));
                  }}
                />
                <Input
                  label="To Date"
                  type="date"
                  name="toDate"
                  min={searchRequest.fromDate}
                  value={searchRequest.toDate}
                  disabled={!searchRequest.fromDate}
                  onChange={(e) => {
                    setSearchRequest((prev) => ({
                      ...prev,
                      toDate: e.target.value,
                    }));
                  }}
                />
                <Dropdown
                  label="Status"
                  options={statusOptions}
                  style={{ height: "20px" }}
                  value={searchRequest.status}
                  onChange={(e, { value }) => {
                    setSearchRequest((prev) => ({
                      ...prev,
                      status: value,
                    }));
                  }}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div className="field" style={{ width: "100%" }}>
                  <Button
                    template="search"
                    onClick={(e) => {
                      handleSearch(e);
                    }}
                  />
                  <Button
                    template="searchReset"
                    onClick={(e) => {
                      handleReset(e);
                    }}
                  />
                </div>
              </div>
            </form>
          </div>
          <div
            className={`title ${activeAccordion === 2 ? "active" : ""}`}
            onClick={() => {
              setActiveAccordion((prev) => {
                if (prev === 2) {
                  return null;
                }

                setSearchClicked(false);
                setSearchResults([]);
                return 2;
              });
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                Advance Search
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>
            </div>
          </div>
          <div className={`content ${activeAccordion === 2 ? "active" : ""}`}>
            <form className="ui form">
              <div className="field">
                <div className="ui icon input">
                  <input
                    type="text"
                    placeholder="Request Numbers"
                    name="reqnos"
                    value={advancedReqNos}
                    onChange={(e) => setAdvancedReqNos(e.target.value)}
                  />

                  <i className="search icon"></i>

                  <div className="results"></div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    marginTop: "17px",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button template="search" onClick={advancedSearch} />
                    <Button
                      template="searchReset"
                      onClick={advancedSearchReset}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
        {searchClicked &&
          (isLoading ? (
            <LoadingSpinner />
          ) : (
            <Grid
              ref={gridRef}
              isLoading={isLoading}
              content={searchResults}
              multiRowSelection={true}
              onSelectionChanged={handleSelectionChanged}
              onCellEditingStopped={cellEditingSubmitHandler}
              fullRowEdit={false}
              stopEditingWhenCellsLoseFocus={true}
              showActionMenu={selectedRows.length > 0}
              heading="Supplier negotiation approval"
              actionMenuOptions={[
                {
                  key: 2,
                  text: "Approve",
                  value: 2,
                },
                {
                  key: 3,
                  text: "Reject",
                  value: 3,
                },
              ]}
              handleActionMenuChange={actionMenuHandler}
              config={[
                {
                  headerName: "Code",
                  field: "jotCode",
                  headerCheckboxSelection: true,
                  checkboxSelection: true,
                },
                {
                  headerName: "Job",
                  field: "jotJobCode",
                },
                {
                  headerName: "Request",
                  field: "jotRequestCode",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Supplier",
                  field: "jotStakeholderName",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Category",
                  field: "jotCategoryValue",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Type",
                  field: "jotTypeValue",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Mode",
                  field: "jotModeValue",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Amount",
                  field: "jotCharge",
                  editable: (params) =>
                    params.data.jotCharge !== null &&
                    params.data.jotCharge !== undefined &&
                    params.data.jotCharge !== 0,
                  cellEditor: "agNumberCellEditor",
                },
                {
                  headerName: "Qty",
                  field: "jotQty",
                  editable: (params) =>
                    params.data.jotQty !== null &&
                    params.data.jotQty !== undefined &&
                    params.data.jotQty !== 0,
                  cellEditor: "agNumberCellEditor",
                },
                {
                  headerName: "Status",
                  field: "jotStatusValue",
                  cellRenderer: StatusCellStyle,
                },
                {
                  headerName: "Active",
                  field: "jotActiveValue",
                  cellRenderer: ActiveCellStyle,
                },
                {
                  headerName: "Pick Up Location",
                  field: "pickupLocationName",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Delivery Location",
                  field: "deliveryLocationName",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Via Location",
                  field: "viaLocations",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Customer Name",
                  field: "customerName",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Vehicle Type",
                  field: "vehicleType",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Actual Vehicle Type",
                  field: "actualVehicleType",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Vehicle Assigned By",
                  field: "vehicleAssignedBy",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Creator Remark",
                  field: "creatorRemark",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Chargeable KM",
                  field: "chargeableJobKm",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Arrived At Pickup Point",
                  field: "arrivedAtPickupTime",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Other Requests",
                  field: "otherRequest",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Negotiation Remark",
                  field: "jotDescriptionn",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
              ]}
            />
          ))}
      </div>
    </>
  );
};

export default SupplierNegotiationApproval;
